import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Bug, Camera, Maximize2, Trash2, Undo2 } from "lucide-react"
import { ModalShell } from "@/components/portal/ModalShell"
import { toast } from "@/components/portal/Toast"
import { sammleKontext, type ProblemKontext } from "@/lib/problemKontext"
import { bildschirmfoto, bildMitBalken, balkenAusPunkten, type Balken } from "@/lib/bildschirmfoto"

/**
 * „Problem melden" — von jeder Seite aus (Baustein 17, #145).
 *
 * Bisher landeten Meldungen in Slack, oft als Screenshot mit Namen, Adressen
 * und Mailtexten von Endkunden. Hier kommt der Kontext automatisch mit (Seite,
 * IDs, Mandant, letzte Fehler), und ein Bildschirmfoto ist optional und
 * bereits geschwärzt — vor dem Senden sieht man genau, was mitgeht.
 *
 * Die Meldung landet im Backlog-Eingang, wie eine Slack-Nachricht.
 */
/**
 * Über ALLEN Pop-ups (höchstes bisher: 9999), damit man auch aus einem
 * offenen Fenster heraus melden kann — z. B. aus den Anruf-Details. Das
 * Meldefenster selbst liegt noch darüber.
 */
export const EBENE_KNOPF = 10050
const EBENE_FENSTER = 10100
const EBENE_GROSS = 10200

export function ProblemMeldenKnopf({ schwebend = false }: { schwebend?: boolean }) {
  const [offen, setOffen] = useState(false)
  const lage: React.CSSProperties = schwebend
    // Auf der Mailliste ist oben rechts kein Platz — dort über der Chat-Blase
    ? { position: "fixed", right: 30, bottom: 84, zIndex: EBENE_KNOPF }
    // Neben „Interne Aufgabe" (right 68) und der Lupe (right 24)
    : { position: "fixed", top: "calc(12px + var(--stoerung-h, 0px))", right: 112, zIndex: EBENE_KNOPF }
  // Manche Pop-ups schließen bei Klick außerhalb (mousedown am document).
  // Der Klick auf den Knopf darf das Pop-up nicht schließen — sonst ist
  // genau das weg, was gemeldet werden soll.
  const nichtWeiter = (e: React.SyntheticEvent) => e.stopPropagation()
  return (
    <>
      <div style={lage} data-kein-foto onMouseDown={nichtWeiter} onPointerDown={nichtWeiter} onClick={nichtWeiter}>
        <button
          type="button"
          onClick={() => setOffen(true)}
          title="Problem melden — mit Seite und Fehlern, Kundendaten geschwärzt"
          aria-label="Problem melden"
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 36, height: 36, borderRadius: schwebend ? "50%" : 8,
            background: "#fff", border: "1px solid #e5e7eb",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)", cursor: "pointer", color: "#64748b",
          }}
        >
          <Bug className="w-4 h-4" />
        </button>
      </div>
      {offen && <ProblemMeldenModal onClose={() => setOffen(false)} />}
    </>
  )
}

function kontextZeilen(k: ProblemKontext): [string, string][] {
  const ids = Object.entries(k.ids).map(([a, b]) => `${a} ${b}`).join(", ")
  return [
    ["Seite", k.route],
    ["Kennungen", ids || "—"],
    ["Mandant", k.tenant || "—"],
    ...(k.popup ? [["Pop-up", k.popup] as [string, string]] : []),
    ["Fehler zuletzt", k.fehler.length ? `${k.fehler.length} (z. B. ${k.fehler[k.fehler.length - 1].text.slice(0, 80)})` : "keine"],
    ["Browser", `${k.fenster} · ${k.browser.slice(0, 60)}`],
    ["Fassung", k.fassung || "—"],
  ]
}

export function ProblemMeldenModal({ onClose }: { onClose: () => void }) {
  // Kontext beim Öffnen festhalten — danach entstehen beim Tippen keine neuen
  // Fehler, die mit dem Problem nichts zu tun haben.
  const [kontext] = useState<ProblemKontext>(() => sammleKontext())
  const [wasPassiert, setWasPassiert] = useState("")
  const [erwartet, setErwartet] = useState("")
  const [bild, setBild] = useState<string | null>(null)
  const [balken, setBalken] = useState<Balken[]>([])
  const [gross, setGross] = useState(false)
  const [nimmtAuf, setNimmtAuf] = useState(false)
  const [sendet, setSendet] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  // Esc schließt nur die Meldung (bzw. erst die große Ansicht) — nicht auch
  // das Pop-up darunter, aus dem gemeldet wird. Darum in der Capture-Phase
  // abfangen, bevor dessen eigener Esc-Lauscher es sieht.
  useEffect(() => {
    const aufEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      e.stopImmediatePropagation()
      if (gross) setGross(false)
      else onClose()
    }
    window.addEventListener("keydown", aufEsc, true)
    return () => window.removeEventListener("keydown", aufEsc, true)
  }, [gross, onClose])

  const aufnehmen = async () => {
    setFehler(null)
    setNimmtAuf(true)
    try {
      setBild(await bildschirmfoto())
      setBalken([])
    } catch (e) {
      setFehler(`Bildschirmfoto ging nicht (${e instanceof Error ? e.message : "unbekannt"}). Die Meldung geht auch ohne.`)
    } finally {
      setNimmtAuf(false)
    }
  }

  const senden = async () => {
    if (wasPassiert.trim().length < 3) {
      setFehler("Bitte kurz beschreiben, was passiert ist.")
      return
    }
    setFehler(null)
    setSendet(true)
    let endBild = bild
    try {
      endBild = bild ? await bildMitBalken(bild, balken) : null
    } catch (e) {
      // Nie ohne die selbst gesetzten Balken senden
      setFehler(`Balken ließen sich nicht einzeichnen (${e instanceof Error ? e.message : "unbekannt"}). Bitte Bild entfernen.`)
      setSendet(false)
      return
    }
    try {
      const r = await fetch("/api/v1/backlog/melden", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          was_passiert: wasPassiert.trim(),
          erwartet: erwartet.trim() || null,
          kontext,
          bild: endBild,
        }),
      })
      if (!r.ok) {
        let text = `HTTP ${r.status}`
        try { text = (await r.json())?.detail || text } catch { /* egal */ }
        throw new Error(typeof text === "string" ? text : `HTTP ${r.status}`)
      }
      toast("Danke! Die Meldung ist im Backlog-Eingang.", "success")
      onClose()
    } catch (e) {
      setFehler(`Senden fehlgeschlagen: ${e instanceof Error ? e.message : "unbekannt"}`)
    } finally {
      setSendet(false)
    }
  }

  return createPortal(
    // Klicks in der Meldung sind für das Pop-up darunter „außerhalb“ —
    // nicht bis zum document durchlassen, sonst schließt es sich.
    <div data-kein-foto onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <ModalShell
        open
        onClose={onClose}
        breite={620}
        ebene={EBENE_FENSTER}
        titel="Problem melden"
        untertitel="Geht in den Backlog. Seite, Kennungen und letzte Fehler kommen automatisch mit."
        fuss={
          <>
            {fehler && <span role="alert" style={{ color: "#b91c1c", fontSize: 12.5, flex: "1 1 100%" }}>{fehler}</span>}
            <div style={{ flex: 1 }} />
            <button type="button" className="pp-btn pp-btn-sm" onClick={onClose}>Abbrechen</button>
            <button
              type="button"
              className="pp-btn pp-btn-sm pp-btn-primary"
              onClick={senden}
              disabled={sendet || nimmtAuf}
            >
              {sendet ? "Sendet…" : "Meldung senden"}
            </button>
          </>
        }
      >
        <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, fontWeight: 600 }}>
            Was ist passiert?
            <textarea
              value={wasPassiert}
              onChange={(e) => setWasPassiert(e.target.value)}
              rows={4}
              autoFocus
              placeholder="z. B. „Beim Speichern des Termins kommt eine Fehlermeldung“"
              className="pp-input"
              style={{ fontWeight: 400, fontSize: 13.5 }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, fontWeight: 600 }}>
            Was hast du erwartet? <span style={{ fontWeight: 400, color: "#64748b" }}>(optional)</span>
            <textarea
              value={erwartet}
              onChange={(e) => setErwartet(e.target.value)}
              rows={2}
              className="pp-input"
              style={{ fontWeight: 400, fontSize: 13.5 }}
            />
          </label>
          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
            Bitte keine Kundennamen oder Telefonnummern ins Textfeld — die Kennungen
            der Seite (Mail, Vorgang, Anruf) gehen automatisch mit.
          </div>

          <details style={{ fontSize: 12.5 }}>
            <summary style={{ cursor: "pointer", color: "#334155" }}>Was automatisch mitgeht</summary>
            <dl data-testid="melden-kontext" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 12px", margin: "8px 0 0" }}>
              {kontextZeilen(kontext).map(([a, b]) => (
                <div key={a} style={{ display: "contents" }}>
                  <dt style={{ color: "#64748b" }}>{a}</dt>
                  <dd style={{ margin: 0, wordBreak: "break-word" }}>{b}</dd>
                </div>
              ))}
            </dl>
          </details>

          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
            {!bild ? (
              <button type="button" className="pp-btn pp-btn-sm" onClick={aufnehmen} disabled={nimmtAuf}>
                <Camera className="w-3.5 h-3.5" />
                {nimmtAuf ? "Nimmt auf…" : "Bildschirmfoto anhängen (Kundendaten geschwärzt)"}
              </button>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 12, color: "#334155" }}>
                  Vorschau — <strong>grau = automatisch geschwärzt</strong>. Noch etwas
                  Kundenbezogenes zu sehen? <strong>Mit der Maus einen Balken darüberziehen.</strong>
                </div>
                <BalkenVorschau bild={bild} balken={balken} onBalken={(z) => setBalken((alt) => [...alt, z])} />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button type="button" className="pp-btn pp-btn-sm pp-btn-primary" onClick={() => setGross(true)}>
                    <Maximize2 className="w-3.5 h-3.5" /> Groß ansehen und schwärzen
                  </button>
                  <button
                    type="button"
                    className="pp-btn pp-btn-sm"
                    onClick={() => setBalken((alt) => alt.slice(0, -1))}
                    disabled={!balken.length}
                  >
                    <Undo2 className="w-3.5 h-3.5" /> Letzten Balken zurück
                  </button>
                  <button type="button" className="pp-btn pp-btn-sm" onClick={() => { setBild(null); setBalken([]) }}>
                    <Trash2 className="w-3.5 h-3.5" /> Bild entfernen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalShell>
      {gross && bild && (
        <GrossAnsicht
          bild={bild}
          balken={balken}
          onBalken={(z) => setBalken((alt) => [...alt, z])}
          onZurueck={() => setBalken((alt) => alt.slice(0, -1))}
          onFertig={() => setGross(false)}
        />
      )}
    </div>,
    document.body,
  )
}

/**
 * Bild in Originalgröße über dem ganzen Fenster (scrollbar), damit man
 * kleine Schrift erkennt und Balken genau setzen kann. Dieselben Balken wie
 * in der kleinen Vorschau. Escape schließt NUR diese Ansicht, nicht die Meldung.
 */
function GrossAnsicht({ bild, balken, onBalken, onZurueck, onFertig }: {
  bild: string
  balken: Balken[]
  onBalken: (z: Balken) => void
  onZurueck: () => void
  onFertig: () => void
}) {
  useEffect(() => {
    const aufEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      e.stopImmediatePropagation()     // sonst schließt ModalShell die ganze Meldung
      onFertig()
    }
    window.addEventListener("keydown", aufEsc, true)
    return () => window.removeEventListener("keydown", aufEsc, true)
  }, [onFertig])

  return (
    <div
      role="dialog"
      aria-label="Bildschirmfoto groß"
      style={{ position: "fixed", inset: 0, zIndex: EBENE_GROSS, background: "rgba(15,23,42,0.92)", display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", color: "#fff", fontSize: 13, flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 200 }}>
          Mit der Maus Balken über alles ziehen, was Kunden betrifft. Grau ist schon geschwärzt.
        </span>
        <button type="button" className="pp-btn pp-btn-sm" onClick={onZurueck} disabled={!balken.length}>
          <Undo2 className="w-3.5 h-3.5" /> Letzten Balken zurück
        </button>
        <button type="button" className="pp-btn pp-btn-sm pp-btn-primary" onClick={onFertig}>
          Fertig
        </button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", textAlign: "center" }}>
        <BalkenVorschau bild={bild} balken={balken} onBalken={onBalken} gross />
      </div>
    </div>
  )
}

/**
 * Vorschau mit selbst gesetzten schwarzen Balken. Hier nur angezeigt (als
 * Überzug); fest ins Bild gemalt werden sie beim Senden (`bildMitBalken`).
 * Balken in Anteilen 0–1, damit die Anzeigegröße keine Rolle spielt.
 */
function BalkenVorschau({ bild, balken, onBalken, gross = false }: {
  bild: string
  balken: Balken[]
  onBalken: (z: Balken) => void
  /** Originalgröße statt auf den Dialog verkleinert */
  gross?: boolean
}) {
  const flaeche = useRef<HTMLDivElement>(null)
  // Startpunkt sofort im Ref (nicht erst nach dem nächsten Rendern) —
  // schnelle Ereignisse hintereinander sehen ihn sonst noch nicht.
  const startRef = useRef<{ x: number; y: number } | null>(null)
  const [start, setStart] = useState<{ x: number; y: number } | null>(null)
  const [jetzt, setJetzt] = useState<{ x: number; y: number } | null>(null)

  const punkt = (e: React.PointerEvent) => {
    const r = flaeche.current!.getBoundingClientRect()
    return { x: (e.clientX - r.left) / (r.width || 1), y: (e.clientY - r.top) / (r.height || 1) }
  }
  const zeichne = (z: Balken, key: string | number, entwurf = false) => (
    <div
      key={key}
      data-testid={entwurf ? "balken-entwurf" : "balken"}
      style={{
        position: "absolute",
        left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.b * 100}%`, height: `${z.h * 100}%`,
        background: entwurf ? "rgba(0,0,0,0.6)" : "#000",
        pointerEvents: "none",
      }}
    />
  )

  return (
    <div
      ref={flaeche}
      data-testid={gross ? "balken-flaeche-gross" : "balken-flaeche"}
      // inline-block: die Fläche ist genau so groß wie das Bild → Anteile stimmen
      style={{ position: "relative", display: "inline-block", maxWidth: gross ? "none" : "100%", textAlign: "left", cursor: "crosshair", touchAction: "none", userSelect: "none", lineHeight: 0 }}
      onPointerDown={(e) => {
        e.preventDefault()
        try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* jsdom */ }
        const p = punkt(e)
        startRef.current = p
        setStart(p)
        setJetzt(p)
      }}
      onPointerMove={(e) => { if (startRef.current) setJetzt(punkt(e)) }}
      onPointerUp={(e) => {
        const a = startRef.current
        if (a) {
          const z = balkenAusPunkten(a, punkt(e))
          if (z.b > 0.005 && z.h > 0.005) onBalken(z)   // Klick ohne Ziehen: nichts
        }
        startRef.current = null
        setStart(null)
        setJetzt(null)
      }}
      onPointerCancel={() => { startRef.current = null; setStart(null); setJetzt(null) }}
    >
      <img
        src={bild}
        alt={gross ? "Bildschirmfoto groß" : "Vorschau Bildschirmfoto (geschwärzt)"}
        draggable={false}
        style={{ maxWidth: gross ? "none" : "100%", display: "block", outline: "1px solid #e5e7eb", borderRadius: 6 }}
      />
      {balken.map((z, i) => zeichne(z, i))}
      {start && jetzt && zeichne(balkenAusPunkten(start, jetzt), "entwurf", true)}
    </div>
  )
}
